import React, { useState } from "react";
import RakimCard from "../components/RakimCard";
import DetailModal from "../components/DetailModal";
import AssessmentModal from "../components/AssessmentModal";
import { DEFINITIONS } from "../data/definitions";

export default function PlanView({ onOpenBooking }) {
  const [openIdx, setOpenIdx] = useState(null);

  // --- STATE: DEEP DIVE MODAL ---
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);

  // --- STATE: ASSESSMENT MODAL ---
  const [assessmentOpen, setAssessmentOpen] = useState(false);

  // --- HANDLER: CHIP CLICK ---
  const handleChipClick = (topicString) => {
    // 1. Try to find exact match in dictionary
    // We check if DEFINITIONS exists first to be safe
    let data =
      DEFINITIONS && DEFINITIONS[topicString] ? DEFINITIONS[topicString] : null;

    // 2. If not found, use a robust fallback structure so it doesn't crash
    if (!data) {
      data = {
        title: topicString,
        definition: "A strategic component of the Simpler System.",
        protocol: "Focus on consistency and progressive overload.",
        standard: "Track weekly adherence.",
        tags: ["System", "Protocol", "Foundation"], // Prevents crash
      };
    }

    setSelectedTopic(data);
    setDetailModalOpen(true);
  };

  // ALIGNMENT: 6 Steps matching the "Build a House" construction phases
  // CRITICAL UPDATE: The strings in 'training' now match your definitions.js EXACTLY.
  const lens = [
    {
      tag: "1 — Assessment",
      hook: "We don't build on a swamp.",
      house: "Site Survey",
      // UPDATED: Matches "Movement screen", "Lifestyle review", "Injury history"
      training: ["Movement screen", "Lifestyle review", "Injury history"],
      mindset: ["Identify constraints", "No guessing"],
      punch: "Clarity first.",
    },
    {
      tag: "2 — Clear Land",
      hook: "Remove the friction.",
      house: "Site Prep",
      // UPDATED: Matches "Mobility work", "Correctives"
      training: ["Mobility work", "Correctives", "Sleep Rhythm"],
      mindset: ["Fix the bucket", "Stop the leaks"],
      punch: "Prepare to load.",
    },
    {
      tag: "3 — Blueprints",
      hook: "New goals need new defaults.",
      house: "Architecture & Systems",
      // UPDATED: Matches "Structured plan", "Progressive overload", "Pain-aware"
      training: ["Structured plan", "Progressive overload", "Pain-aware"],
      mindset: ["Systems > motivation", "Pain-aware progressions"],
      punch: "Not hype. Architecture.",
    },
    {
      tag: "4 — Foundation",
      hook: "Earn the right to load.",
      house: "Pour Concrete",
      // UPDATED: Matches "Core stability", "Form & posture", "Protein baseline"
      training: ["Core stability", "Form & posture", "Protein baseline"],
      mindset: ["Stability > Intensity", "Show up daily"],
      punch: "Build the base.",
    },
    {
      tag: "5 — Finish Work",
      hook: "Optimization & Detail.",
      house: "Interiors & Systems",
      // Note: These don't have definitions yet, so they will use the "Fallback" popup
      training: ["Hypertrophy", "Body Comp", "Zone 2 Cardio"],
      mindset: ["Refine the machine", "Dial in nutrition"],
      punch: "Look like the work.",
    },
    {
      tag: "6 — Maintenance",
      hook: "Protect the asset.",
      house: "Live in it",
      // Note: These don't have definitions yet, so they will use the "Fallback" popup
      training: ["Recovery Cycles", "Deload Weeks", "Life Integration"],
      mindset: ["Consistency is the metric", "Play the long game"],
      punch: "Built to last.",
    },
  ];

  return (
    <div style={{ paddingBottom: 80, animation: "fadeIn 0.4s ease-out" }}>
      {/* 1. BRAND STRIP */}
      <div className="blueprint">
        <div className="container">
          <div className="brandStrip">
            <div>
              <div className="brandLockup" style={{ marginBottom: 16 }}>
                SIMPLER // BUILD_SEQUENCE{" "}
                <span style={{ opacity: 0.3 }}>:: ACTIVE</span>
              </div>
              <h1
                style={{
                  fontSize: "clamp(32px, 5vw, 48px)",
                  maxWidth: 600,
                  margin: "0 0 16px",
                  lineHeight: "1.1",
                  fontWeight: "800",
                }}
              >
                We don't do random. <br />
                <span style={{ color: "var(--brand)" }}>
                  We build the house.
                </span>
              </h1>
              <p
                className="muted"
                style={{ maxWidth: 500, fontSize: 16, lineHeight: "1.6" }}
              >
                You can't frame the walls until you clear the land. This is the
                order of operations for a body you can live in.
              </p>
            </div>

            {/* KPI Chips */}
            <div className="kpis">
              <div className="kpi">15-min blocks</div>
              <div className="kpi">Pain-aware</div>
              <div className="kpi">Structure first</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* 2. PLANNING LENS SECTION */}
        <div
          className="secHead"
          style={{
            marginTop: 40,
            marginBottom: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div style={{ maxWidth: 680 }}>
            <div
              className="chip"
              style={{
                marginBottom: 12,
                background: "rgba(245, 158, 11, 0.1)",
                color: "#f59e0b",
                border: "1px solid rgba(245, 158, 11, 0.2)",
              }}
            >
              <span className="dot" style={{ background: "#f59e0b" }} />{" "}
              CONSTRUCTION PHASES
            </div>
            <h3 style={{ marginBottom: 8, fontSize: "24px" }}>
              The <span style={{ color: "#fff" }}>Build Sequence.</span>
            </h3>
            <p className="muted" style={{ margin: 0, fontSize: 14 }}>
              Tap any phase to see the protocols and big rocks.
            </p>
          </div>

          <button
            className="btn primary"
            onClick={() => setAssessmentOpen(true)}
            style={{ padding: "12px 24px", fontSize: "14px" }}
          >
            Run Diagnostic
          </button>
        </div>

        {/* --- THE RAKIM GRID --- */}
        <div
          className="cards"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "16px",
          }}
        >
          {lens.map((item, i) => (
            <RakimCard
              key={i}
              idx={i}
              item={item}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? null : i)}
              onChipClick={handleChipClick}
            />
          ))}
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: 60,
            marginBottom: 60,
            padding: "40px",
            background: "rgba(255,255,255,0.02)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          <div
            style={{
              fontSize: "18px",
              fontStyle: "italic",
              color: "#fff",
              marginBottom: "16px",
            }}
          >
            “You don’t change by force. You change by awareness—then
            construction.”
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--muted)",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            — The Simpler Philosophy
          </div>
        </div>

        {/* 3. DETAILED SPECS */}
        <div className="secHead">
          <h3>System Specs</h3>
        </div>

        <div
          className="cards"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "16px",
          }}
        >
          {[
            [
              "Delivery",
              "In-Home or Remote",
              "We come to you (local) or build your plan digitally. The standard remains the same.",
            ],
            [
              "Cadence",
              "15-Minute Blocks",
              "We don't do hour-long slogs. We stack 15-minute micro-sessions that fit your actual day.",
            ],
            [
              "Tools",
              "Low Tech / High Signal",
              "Kettlebells, bodyweight, and bands. We focus on the movement, not the machine.",
            ],
          ].map(([tag, title, desc], i) => (
            <div key={i} className="glass" style={{ padding: "24px" }}>
              <div
                className="muted"
                style={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: 8,
                  color: "var(--brand)",
                }}
              >
                {tag}
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "#fff",
                }}
              >
                {title}
              </div>
              <p
                className="muted"
                style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}
              >
                {desc}
              </p>
            </div>
          ))}

          {/* Offer Card */}
          <div
            className="glass highlight"
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "24px",
              borderColor: "var(--brand)",
              background: "rgba(34,197,94,0.05)",
            }}
          >
            <div
              className="muted"
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: 8,
                color: "#fff",
              }}
            >
              Start Building
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 700,
                marginBottom: 16,
                color: "#fff",
              }}
            >
              Ready to break ground?
            </div>
            <ul
              className="muted"
              style={{
                fontSize: 14,
                paddingLeft: 20,
                margin: "0 0 24px 0",
                lineHeight: 1.8,
                color: "#ccc",
              }}
            >
              <li>Diagnostic Screen — $149</li>
              <li>1:1 Coaching — $120 / session</li>
              <li>Remote Programming — $199 / mo</li>
            </ul>
            <button
              className="btn primary"
              style={{
                width: "100%",
                justifyContent: "center",
                marginTop: "auto",
              }}
              onClick={onOpenBooking}
            >
              Book Diagnostic
            </button>
          </div>
        </div>
      </div>

      {/* --- MODALS --- */}
      <DetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        data={selectedTopic}
      />

      <AssessmentModal
        open={assessmentOpen}
        onClose={() => setAssessmentOpen(false)}
      />
    </div>
  );
}
